import { useMemo } from "react";
import IconStar from "@/assets/icons/star-filled.svg?react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/utils";
import { Link } from "react-router";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import DonIcon from "@/assets/icons/don.svg?react";
import {cn} from "@/lib/utils.ts";

interface Props {
  percentage?: number;
  options?: { size: number; strokeWidth: number };
}

const CircleProgress = (props: Props) => {
  const { percentage = 0, options = { size: 44, strokeWidth: 4 } } = props;
  const radius = (options.size - options.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentage / 100);

  return (
    <svg
      className="absolute –inset-3"
      width={options.size}
      height={options.size}
      viewBox={`0 0 ${options.size} ${options.size}`}
    >
      <defs>
        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FBC76C" />
          <stop offset="100%" stopColor="#EBA132" />
        </linearGradient>
      </defs>

      {/* Progress Circle */}
      <circle
        cx={options.size / 2}
        cy={options.size / 2}
        r={radius}
        fill="none"
        stroke="url(#gradient)"
        strokeWidth={options.strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${options.size / 2} ${options.size / 2})`}
        className="transition-[stroke-dashoffset] duration-500 ease-in-out"
      />
    </svg>
  );
};

interface OfferCardProps {
  id: string;
  mode?: "light" | "dark";
  taskCount: number;
  completedTasksCount: number;
  title: string;
  award: string | number;
  bannerSrc?: string;
  totalAward: string | number
}

const OfferCard = (props: OfferCardProps) => {
  const { mode, taskCount, completedTasksCount, title, totalAward, bannerSrc } =
    props;
  const { t } = useTranslation();

  const progressBar = useMemo(() => {
    return Math.min((completedTasksCount / taskCount) * 100, 100);
  }, [completedTasksCount, taskCount]);

  return (
    <div
      className={`${
        mode === "light" ? "bg-[#E8E9E8]" : "bg-gray-4"
      } rounded-xl p-4 relative z-10 overflow-hidden`}
    >
      {mode === "light" && (
        <div className="w-9 h-9 flex-center bg-gray-1 rounded-full mb-7">
          <IconStar color="white" width="20" height="20" />
        </div>
      )}
      <h4
        className={`${
          mode === "light" ? "text-black" : "text-white"
        } text-lg font-bold leading-130 mb-0.5 line-clamp-1`}
      >
        {title}
      </h4>
      <p
        className={cn("text-sm font-medium leading-130 flex-y-center gap-0.5", mode === "light" ? "text-dark" : "text-gray-1")}
      >
        +{formatNumber(totalAward ?? 0)} <DonIcon className="size-[14px]"/>
      </p>
      <div className="flex-between items-end relative z-10">
        <Link to={`/get-don/${props.id}`}>
          <VibratingButton
            className={`${
              mode === "light" ? "bg-black text-white" : "bg-white text-black"
            } flex-y-center pl-2.5 pr-2 py-1.5 rounded-full text-xs leading-120 font-semibold click-button transition-200`}
          >
            {t("more_details")}
            <IconChevron width="16" height="16" strokeWidth="1.4" />
          </VibratingButton>
        </Link>
        <div
          className={`${
            mode === "light"
              ? "bg-white/08 text-dark border-gray-1 backdrop-blur-[6px]"
              : "text-white border-white/[0.08]"
          } w-11 h-11 rounded-full border-2 flex-center text-xs font-semibold leading-120  relative`}
        >
          {completedTasksCount}/{taskCount}
          <CircleProgress percentage={progressBar} />
        </div>
      </div>
      {mode === "light" && (
        <img
          className="h-full absolute right-0 top-0 -z-[1] size-full object-cover"
          src={bannerSrc ?? "images/patterns/special-offers.webp"}
          alt="Special Offers Pattern"
        />
      )}
    </div>
  );
};

export default OfferCard;
